import { IconDots } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { cn } from '#src/cn.ts'
import { SkillGlyph } from '#src/components/ai-elements/Skill/SkillGlyph.tsx'
import { SkillScopeBadge } from '#src/components/ai-elements/Skill/SkillScopeBadge.tsx'
import {
  type SkillItem,
  skillItemTitle,
} from '#src/components/ai-elements/Skill/types.ts'

export function SkillList<TSkill extends SkillItem>({
  skills,
  emptyLabel = 'No skills yet',
  className,
  renderUpdatedAt,
  renderOwner,
  renderActions,
  onSkillClick,
}: {
  skills: TSkill[]
  emptyLabel?: string
  className?: string
  renderUpdatedAt?: (skill: TSkill) => ReactNode
  renderOwner?: (skill: TSkill) => ReactNode
  renderActions?: (skill: TSkill) => ReactNode
  onSkillClick?: (skill: TSkill) => void
}) {
  const hasOwnerColumn = !!renderOwner

  if (skills.length === 0) {
    return (
      <div
        className={cn(
          'flex min-h-44 flex-col items-center justify-center gap-3 rounded-xl border border-dashed text-muted-foreground',
          className
        )}
      >
        <SkillGlyph size="lg" className="opacity-70" />
        <p className="text-sm">{emptyLabel}</p>
      </div>
    )
  }

  return (
    <div className={cn('overflow-hidden rounded-xl border', className)}>
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
          <tr>
            <th className="px-4 py-2.5 text-left font-medium">Skill</th>
            <th className="hidden px-3 py-2.5 text-left font-medium md:table-cell">
              Scope
            </th>
            {hasOwnerColumn && (
              <th className="hidden px-3 py-2.5 text-left font-medium lg:table-cell">
                Owner
              </th>
            )}
            <th className="hidden px-3 py-2.5 text-left font-medium lg:table-cell">
              Updated
            </th>
            <th className="w-10 px-2 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {skills.map((skill) => (
            <tr
              key={skill.id}
              className={cn(
                'border-b transition-colors last:border-0',
                onSkillClick && 'cursor-pointer hover:bg-muted/50'
              )}
              onClick={() => onSkillClick?.(skill)}
            >
              <td className="px-4 py-3">
                <div className="flex min-w-0 items-start gap-3">
                  <SkillGlyph size="sm" />
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate font-medium">
                      {skillItemTitle(skill)}
                    </span>
                    {skill.description && (
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {skill.description}
                      </p>
                    )}
                  </div>
                </div>
              </td>
              <td className="hidden px-3 py-3 md:table-cell">
                <SkillScopeBadge scope={skill.scope} />
              </td>
              {hasOwnerColumn && (
                <td className="hidden px-3 py-3 text-muted-foreground lg:table-cell">
                  {renderOwner?.(skill)}
                </td>
              )}
              <td className="hidden px-3 py-3 text-muted-foreground lg:table-cell">
                {renderUpdatedAt?.(skill)}
              </td>
              <td className="px-2 py-3">
                {renderActions?.(skill) ?? (
                  <IconDots className="size-4 text-transparent" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
